import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormArray, FormControl } from '@angular/forms';

import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { AppHelper } from '@app/core/classes/app-helper';
import { AppConfig } from '@app/core/classes/app-config';
import { RootService } from '@app/core/root.service';
import { environment } from '@env/environment';
import { transition, trigger, useAnimation } from '@angular/animations';
import { HideAnimation, showAnimation } from '@app/shared/animations/transform-opacity';
import { ApiResponse, ItemProps } from '@app/interfaces';
import { GroupByPipe } from 'ngx-pipes';
import { SharedCoreFormService } from '@app/shared/services/shared-core-form.service';
import { MatStepper } from '@angular/material/stepper';
// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
// tslint:disable-next-line:no-duplicate-imports
import * as _moment from 'moment';
import { defaultFormat as _rollupMoment } from 'moment';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PairsPipe, SHARED_IMPORTS } from '@app/shared/shared.imports';
import { FormFooterComponent } from '@app/shared/components/form-footer/form-footer.component';
import { TranslateModule } from '@ngx-translate/core';
import { NewBreadcrumbComponent } from '@app/shared/components/new-breadcrumb/new-breadcrumb.component';

const moment = _rollupMoment || _moment;

@Component({
  selector: 'app-core-form',
  templateUrl: './core-form.component.html',
  styleUrls: ['./core-form.component.scss'],
  standalone: true,
  imports: [...SHARED_IMPORTS, TranslateModule, FormFooterComponent, PairsPipe, NewBreadcrumbComponent],
  animations: [
    trigger('showHide', [
      transition('void => *', [
        useAnimation(showAnimation, {
          params: {
            timings: '200ms ease-in-out',
            transform: 'translateY(-10%)',
            opacity: '0'
          }
        })
      ]),
      transition('* => void', [
        useAnimation(HideAnimation, {
          params: {
            timings: '200ms 200ms ease-in-out',
            transform: 'translateY(-10%)',
            opacity: '0'
          }
        })
      ])
    ])
  ],
  providers: [GroupByPipe]
})
export class CoreFormComponent implements OnInit, OnDestroy {
  formTouched: boolean;
  steperComplete: boolean;
  stepperMode: boolean;
  loaded: boolean;
  //if you want to show loading in add form
  LoadedAddForm: boolean = true;

  @ViewChild('stepper') stepper: MatStepper;
  @ViewChild('formRef') formRef: any;
  showBreadcrumb = true;
  headerSmallSize = false;

  page_heading = true;

  gridGapSize = '0px';
  cid: string;
  form: FormGroup<any>;
  isEdit = false;
  itemId: number;
  item: any;
  config = AppConfig;
  helper = AppHelper;
  isSubmitted = false;
  isClone = false;
  isSaveAddNew = false;
  environment = environment;
  imageFieldName: string;
  previewImage: string;
  fileFields: string[] = [];
  alive = true;
  editId: number = null;
  updateAsPostExternal = false;
  formValid: boolean = true;
  /**
   * inputsVisibility determines visibility of inputs in each form and will be updated
   * based on each condition from fieldShowConditions function that is executed based on
   * featureProps showConditions
   */
  inputsVisibility: {
    key: string;
    visible?: boolean;
  }[] = [];

  protected _lists: any = [];
  constructor(
    public service: RootService,
    protected fb: FormBuilder,
    protected activatedRoute: ActivatedRoute,
    public groupByPipe: GroupByPipe,
    public sharedCoreFormService: SharedCoreFormService,
    // public changeDetectorRef: ChangeDetectorRef,
    // private dialogRefOnly: MatDialog,
    public dialogRef: MatDialogRef<CoreFormComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {
    this.cid = service.cid;
  }

  /**
   * Identify the Form fields in each form controller
   */
  get formFields(): any {
    return this.mapFields(this.service.featureProps);
  }

  mapFields(fieldsList: ItemProps[]) {
    let formFields = {};
    for (const field of fieldsList) {
      if (field.form) {
        if (field.form.formFieldType === 'formArray') {
          formFields = {
            ...formFields,
            [field.name]: this.fb.array([this.fb.group(this.mapFields(field.form.arrayForm))])
          };
        } else {
          formFields = {
            ...formFields,
            [field.name]: [
              {
                value: field.form.initValue || field.form.initValue === 0 ? field.form.initValue : null,
                disabled: (field.form.editDisabled && this.isEdit) || field.form.disabled
              },
              field.form.Validators
            ]
          };
        }
        this.inputsVisibility.push({
          key: field.name,
          visible: true
        });
      }
    }
    // console.log(formFields);

    return formFields;
  }

  get lists() {
    return this._lists;
  }

  set lists(value: any) {
    this._lists = value;
  }

  ngOnInit(): void {
    // check if data loaded or not
    this.service.loaded.pipe(takeWhile(() => this.alive)).subscribe((loaded: boolean) => (this.loaded = loaded));
    this.service.loadSelectLists('form');

    // show loading in create form
    // this.service.listData.pipe(takeWhile(() => this.alive)).subscribe(createData => {
    //   if ((createData.dataUrl = 'roles/permissions' && !this.itemId)) this.addFormLoading = true;
    // });

    this.checkFormType();
    this.groupItemsByGroup();
    this.createForm();

    this.loadResourcesSubscription();
    this.updateFormFieldSubscription();
    /**
     * check for route data flag
     */
    this.activatedRoute.data.pipe(takeWhile(() => this.alive)).subscribe(routeData => {
      if (routeData.isClone) {
        this.isClone = true;
      } else if (routeData.isEdit) {
        this.isEdit = true;
        // this.previewImage = this.form.controls.photo ? this.form.controls.photo.value : null;
        // this.form.removeControl('photo');
      }
    });

    this.checkPasswords();
    // this.clearInput();
    this.permissionsUpdateSubscription();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  loadResourcesSubscription() {
    /**
     * Check if path contain ID
     * return values from item id values
     */
    this.activatedRoute.params.pipe(takeWhile(() => this.alive)).subscribe((routeInfo: any) => {
      if (routeInfo.id) {
        this.loadResources(routeInfo.id);
      }
    });
    if (this.dialogRef && this.dialogData && this.dialogData.id) {
      this.loadResources(this.dialogData.id);
    }
  }

  createArrayItem(formArray: ItemProps[], name: string) {
    const fields = this.mapFields(formArray);
    // console.log(fields);
    return this.fb.group(fields);
  }

  addArrayItem(formArray: ItemProps[], name: string): void {
    const items = this.form.get(name) as FormArray;
    let lastItem: FormGroup;
    if (!this.editId && this.editId != 0) {
      lastItem = items['controls'][items.controls.length - 1] as FormGroup;
      // console.log(name);
    } else {
      lastItem = items['controls'][this.editId] as FormGroup;
    }
    // this.validateAllFormFields(lastItem);
    Object.keys(lastItem.controls).forEach(field => {
      // {1}
      const control = lastItem.get(field); // {2}
      console.log(6);
      control.markAsTouched({ onlySelf: true }); // {3}
      control.markAsDirty({ onlySelf: true }); // {3}
    });
    if (lastItem.valid) {
      if (!this.editId && this.editId != 0) {
        items.push(this.createArrayItem(formArray, name));
      }
      this.handleToggle(items, this.editId || items.controls.length - 2, lastItem);
      this.editId = null;
    }
  }
  handleToggle(array: FormArray, index: number, lastItem: FormGroup) {
    return;
  }

  removeArrayItem(name: string, index: number) {
    const items = this.form.get(name) as FormArray;
    items.removeAt(index);
  }
  /**
   * Pick color
   * @param field: field name
   * @param color: HEX color
   */
  setColor(field: string, color: string) {
    this.form.controls[field].setValue(color);
  }

  getForm(name: string) {
    // console.log(this.form.get(name))
    return this.form.get(name);
  }

  getArrayForm(name: string): FormArray {
    // console.log(name);
    const field = this.form.get(name) as FormArray;
    // console.log(field);

    return field;
    // return field[0].controls;
  }

  getArrayFormControls(name: string) {
    // console.log(name);
    const field = this.form.get(name) as FormArray;
    // console.log(field.controls);

    return field.controls;
    // return field[0].controls;
  }

  stepperRowAction(action: string, name: string, index: number) {
    // console.log(action, index);
    this.editId = null;
    if (action === 'edit') {
      this.editId = index;
    }
    if (action === 'delete') {
      this.removeArrayItem(name, index);
    }
  }
  get Form() {
    return this.form as FormGroup;
  }

  uploadPhoto(event: any, field: any, image?: any) {
    if (this.isEdit) {
      this.uploadNewPhoto(field, image);
      // const photoControl = this.fb.control(null, []);
      // this.form.addControl('photo', photoControl);
    }
    if (field.form.inputValueType === 'base64_img') {
      this.imageFieldName = field.name;
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = this.convertImage.bind(this);
        reader.readAsBinaryString(file);
      }
    } else if (field.form.inputValueType === 'formData') {
      const file = event.target.files[0];
      this.readURL(file);
      this.form.controls[field.name].setValue(file);
    }
  }

  uploadNewPhoto(field: any, image: any) {
    const formV: any = {};
    formV[field.name] = image;
    this.service
      .doCreate(`${this.cid}/${this.itemId}/${field.name}`, this.toFormData(formV))
      .pipe(takeWhile(() => this.alive))
      .subscribe(() => {});

    //   .then(() => {
    //     this.service.updateResources.next(null);
    //   });
  }

  checkFormType() {
    this.activatedRoute.data.pipe(takeWhile(() => this.alive)).subscribe(routeData => {
      if (routeData.isClone) {
        this.isClone = true;
      } else if (routeData.isEdit) {
        this.isEdit = true;
      }
    });

    if (this.dialogRef && this.dialogData && this.dialogData.id) {
      // console.log(this.dialogData.id);
    }
  }

  enableControl(control: any) {
    control.enable();
  }

  /**
   * Preview Image
   * @param input: Input
   */
  readURL(input: any) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.previewImage = e.target.result;
    };
    reader.readAsDataURL(input);
  }

  convertImage(readerEvt: any) {
    const binaryString = readerEvt.target.result;
    this.form.controls[this.imageFieldName].setValue(btoa(binaryString));
  }

  /**
   * Init form lists
   */
  initLists(): void {
    this.lists = [];
  }

  /**
   * fetch single item data from service
   * and fill form with it in Edit forms
   */
  loadResources(id: number): void {
    this.itemId = id;
    this.service
      .showItem(id)
      // .pipe(takeWhile(() => this.alive))
      .pipe(
        map(item => {
          // this.item = item;
          return this.refactorItem(item);
        })
      )
      .subscribe(
        response => {
          const obj = JSON.parse(JSON.stringify(response)); // clone response object
          this.formInjectValues(obj);
          // this.removeFileInputs();
        },
        err => {
          this.service.errorHandle(err);
        }
      );
  }

  /**
   * Do operation on response before subscription
   * @param item: Response
   */
  refactorItem(item: any): any {
    if (this.isClone && item.name) {
      item.name += ' - copy';
    }
    return item;
  }

  trackByFn() {
    return;
  }

  formInjectValues(obj: any) {
    return this.form.patchValue(obj);
  }

  /**
   * Create the form controls
   * param => formFields fills from each inheritance component
   */
  createForm(): void {
    this.form = this.fb.group(this.formFields);
  }

  // removeFileInputs(): void {
  //   if (this.fileFields.length) {
  //     this.fileFields.forEach(x => {
  //       this.form.removeControl(x);
  //     });
  //   }
  // }

  /**
   * Load necessary lists to fill specific fields
   */
  getLists(field: any): Subscription {
    return this.service.getLists(field);
  }

  /**
   * get data and fill & cache it in service
   */
  fillLists(): void {
    for (const item of this.lists) {
      for (const key of Object.keys(item)) {
        this.getLists(key);
      }
    }
  }

  /**
   * submit form data to createItem method in root service
   */
  prepareFormAfterSubmit(): void {
    return;
  }

  refactorFormValue() {
    for (const field of Object.keys(this.form.value)) {
      const xFields = this.service.featureProps.filter(x => x.name === field);
      xFields.forEach(xField => {
        if (xField.form && xField.form.formFieldType) {
          const fieldType = xField.form.formFieldType;

          if (fieldType === 'datepicker') {
            if (this.form.value[xField.name]) {
              this.form.value[xField.name] = this.convertDate(this.form.value[xField.name], 'YYYY-MM-DD');
            }
          }
          /**
           * clears value of input image from form submitted value if user clicks
           * open select then cancels image selection while there is already an image
           * previously selected and patched into form values
           */
          if (fieldType === 'img') {
            if (typeof this.form.value[xField.name] === 'string') {
              delete this.form.value[xField.name];
            }
          }

          if (
            this.form.value[xField.name] === '' ||
            this.form.value[xField.name] === null ||
            (Array.isArray(this.form.value[xField.name]) && this.form.value[xField.name].length === 0) ||
            (Array.isArray(this.form.value[xField.name]) &&
              this.form.value[xField.name].length === 1 &&
              this.form.value[xField.name][0] === '') ||
            JSON.stringify(this.form.value[xField.name]) === '[]' ||
            this.form.value[xField.name] === 'Invalid date' ||
            JSON.stringify(this.form.value[xField.name]) === 'Invalid date'
          ) {
            if (this.isEdit && (fieldType === 'img' || fieldType === 'file_upload_details')) {
              this.form.value[xField.name] = '';
            } else if (this.isEdit) {
              if (fieldType === 'checkbox') {
                delete this.form.value[xField.name];
              } else {
                this.form.value[xField.name] = null;
              }
            } else {
              // console.log(this.form.value[field]);
              if (!this.isEdit) {
                delete this.form.value[xField.name];
              }
            }
          }
        }
      });
    }
    this.refactorFormBeforeSubmit(this.form.value);
  }

  refactorFormBeforeSubmit(formValue: any) {
    return this.service.refactorFormBeforeSubmit(formValue);
  }

  convertDate(date: any, dateType: string) {
    const convertedDate = this.service.shared.momentFormatEn(date, dateType);
    // console.log(convertedDate);
    return convertedDate;
  }

  /**
   * Post form data to API
   */
  formSubmission(saveAddNew?: boolean) {
    this.isSubmitted = true;
    this.prepareFormAfterSubmit();
    let updateAsPost = false;
    let jsonForm = true;

    for (const field of this.service.featureProps) {
      if (
        field.form &&
        (field.form.formFieldType === 'img' ||
          field.form.formFieldType === 'file' ||
          field.form.formFieldType === 'file_upload_details')
      ) {
        updateAsPost = true;
      }
      if (
        field.form &&
        (field.form.formFieldType === 'img' ||
          field.form.formFieldType === 'file' ||
          field.form.formFieldType === 'file_upload_details')
      ) {
        jsonForm = false;
      }
    }

    this.refactorFormValue();

    if (this.isEdit) {
      if (updateAsPost) {
        return this.service
          .updateItemAsPost(this.itemId, this.toFormData(this.form.value, 'PUT'))
          .pipe(takeWhile(() => this.alive))
          .subscribe(
            (resp: ApiResponse) => {
              this.normalizeFlags();
              this.service.updateResources.next(null);
              this.service.shared.toastr.success(
                // this.service.shared.translate.instant(this.cid + '.updated'),
                resp.message,
                this.service.shared.translate.instant('common.success')
              );
              if (saveAddNew) {
                this.form.reset();
              } else {
                return this.navigateToList();
              }
            },
            (error: any) => {
              this.normalizeFlags();
              console.log(error);
              if (error.status === 413) {
                this.service.shared.toastr.error(
                  error.message,
                  this.service.shared.translate.instant('error_codes.413')
                );
              }
              /**
               * handling server side validation errors
               */
              if (error && error.error && error.error.errors) {
                this.ServerValidationErrors(error.error.errors);
              }
            }
          );
      } else if (this.updateAsPostExternal) {
        // console.log('post');
        return this.service
          .updateItemAsPost(this.itemId, this.form.value)
          .pipe(takeWhile(() => this.alive))
          .subscribe(
            (resp: ApiResponse) => {
              this.normalizeFlags();
              this.service.updateResources.next(null);
              this.service.shared.toastr.success(
                // this.service.shared.translate.instant(this.cid + '.updated'),
                resp.message,
                this.service.shared.translate.instant('common.success')
              );
              if (saveAddNew) {
                this.form.reset();
              } else {
                return this.navigateToList();
              }
            },
            (error: any) => {
              this.normalizeFlags();
              console.log(error);

              if (error.status === 413) {
                this.service.shared.toastr.error(
                  error.message,
                  this.service.shared.translate.instant('error_codes.413')
                );
              }
              /**
               * handling server side validation errors
               */
              if (error && error.error && error.error.errors) {
                this.ServerValidationErrors(error.error.errors);
              }
            }
          );
      } else {
        return this.service
          .updateItem(this.itemId, this.form.value)
          .pipe(takeWhile(() => this.alive))
          .subscribe(
            (resp: ApiResponse) => {
              this.normalizeFlags();
              this.service.updateResources.next(null);
              this.service.shared.toastr.success(
                // this.service.shared.translate.instant(this.cid + '.updated'),
                resp.message,
                this.service.shared.translate.instant('common.success')
              );
              if (saveAddNew) {
                this.form.reset();
              } else {
                return this.navigateToList();
              }
            },
            (error: any) => {
              this.normalizeFlags();
              if (error.status === 413) {
                this.service.shared.toastr.error(
                  error.message,
                  this.service.shared.translate.instant('error_codes.413')
                );
              }
              /**
               * handling server side validation errors
               */
              if (error && error.error && error.error.errors) {
                this.ServerValidationErrors(error.error.errors, this.foucsInputs);
              }
            }
          );
      }
    } else {
      this.service
        .createItem(this.toFormData(this.form.value, null, jsonForm))
        .pipe(takeWhile(() => this.alive))
        .subscribe(
          (resp: ApiResponse) => {
            this.normalizeFlags();
            if (resp.data && resp.data.id) {
              this.actionOnCreatedItem(resp.data.id);
            }
            if (this.service.updateOnCreate) {
              this.service.updateResources.next(null);
            }
            if (this.service.appendOnCreate) {
              this.service.appendResources$.next(null);
            }
            this.service.shared.toastr.success(
              // this.service.shared.translate.instant(this.cid + '.created'),
              resp.message,
              this.service.shared.translate.instant('common.success')
            );
            if (saveAddNew) {
              this.form.reset();
            } else {
              return this.navigateToList();
            }
          },
          error => {
            this.normalizeFlags();
            console.log('error', error);

            if (error) {
              /**
               * payload error
               */
              if (error.status === 413) {
                this.service.shared.toastr.error(
                  error.message,
                  this.service.shared.translate.instant('error_codes.413')
                );
              }

              /**
               * timeout error
               */
              if (error && error.name === 'TimeoutError') {
                this.service.shared.toastr.warning(
                  error.message,
                  this.service.shared.translate.instant('error_codes.TimeoutError')
                );
              }
              /**
               * handling server side validation errors
               */
              if (error.error && error.error.errors) {
                // console.log('sdfsdfsdffsd');

                this.ServerValidationErrors(error.error.errors, this.foucsInputs);
              }
            }
          }
        );
    }
  }

  /**
   * add server side validation errors into the errors array in each form control inside key serverError
   * @param errors => taken from fail response 422
   */
  ServerValidationErrors(errors: any, callback?: any) {
    // this.formRef.nativeElement.scrollTop = 0
    if (!errors) {
      return;
    }
    Object.keys(errors).forEach(prop => {
      const formControl = this.getFormControl(prop);
      if (formControl) {
        // activate the error message
        formControl.setErrors({
          serverError: errors[prop]
        });
        // console.log(1);
        formControl.markAsTouched();
      }
    });
    callback();
  }

  foucsInputs = () => {
    for (const key of Object.keys(this.form.controls)) {
      if (this.form.controls[key].invalid) {
        // console.log(this.formRef.nativeElement.elements);
        const invalidControlValue = this.form.controls[key]['value'];
        const invalidControl = [...this.formRef.nativeElement.elements].find(
          (d: any) => d.value === invalidControlValue
        );
        // console.log('invalidControl', invalidControl);
        if (invalidControl) {
          invalidControl.focus();
        }
        break;
      }
    }
  };

  toFormData<T>(formValue: T, _method?: string, jsonForm?: boolean) {
    // console.log(formValue);
    const formData = new FormData();

    for (const key of Object.keys(formValue)) {
      const value = (formValue as any)[key];
      /**
       * fix for boolean values in formData method
       */
      if (value === true) {
        formData.append(key, '1');
      } else if (value === false) {
        formData.append(key, '0');
      } else {
        formData.append(key, value);
      }
      // console.log(formData);

      const xFields = this.service.featureProps.filter(x => x.name === key);
      xFields.forEach(xField => {
        if (xField.form && xField.form.formFieldType) {
          const fieldType = xField.form.formFieldType;

          if (fieldType === 'ng_select_multiple') {
            if (value && value.length) {
              const valueArray = value;
              formData.delete(key);
              valueArray.forEach((id: any) => {
                formData.append(xField.name + '[]', id);
              });
            } else {
              if (this.isEdit) {
                formData.delete(key);
                formData.append(xField.name, '');
              } else {
                formData.append(xField.name + '[]', '');
                formData.delete(key);
              }
            }
          }

          if ((formValue as any)[key] === null) {
            console.log(key);
            console.log((formValue as any)[key]);
            formData.delete(key);
            formData.append(key, '');
          }
        }
      });
    }

    if (_method) {
      formData.append('_method', _method);
    }
    if (jsonForm) {
      return formValue;
    } else {
      return formData;
    }
  }

  // validationErrors(errors: any) {
  //   Object.keys(errors).forEach(prop => {
  //     const formControl = this.form.get(prop);
  //     if (formControl) {
  //       // activate the error message
  //       formControl.setErrors({
  //         serverError: errors[prop]
  //       });
  //     }
  //   });
  // }
  /**
   * alternative to normal show and hide to takes function that's passed from ItemProps
   * and parameters abstract control input to show and hide inputs based on a boolean
   * return from this function and also remove it's validations when hiding and add it back when showing the input
   * @param field : ItemProps form field passed
   */
  fieldShowConditions(field: ItemProps) {
    if (field.form && field.form.showConditions) {
      const validators = field.form.Validators;
      let trueCount = 0;
      if (field.form.showConditions && field.form.showConditions.length) {
        field.form.showConditions.forEach((condition: any) => {
          if (condition.fnProp) {
            if (condition.fnProp.fn) {
              if (condition.fnProp.fn(condition.fnProp.prop ? this.form.controls[condition.fnProp.prop].value : null)) {
                trueCount++;
              }
            }
          }
        });
      }

      if (trueCount === 0) {
        if (this.inputsVisibility.find(x => x.key === field.name).visible) {
          this.inputsVisibility.find(x => x.key === field.name).visible = false;
          this.getFormControl(field.name).clearValidators();
          this.getFormControl(field.name).updateValueAndValidity();
          console.log(field.name, 'clearValidators');
        }
        return false;
      } else if (trueCount === 1) {
        if (!this.inputsVisibility.find(x => x.key === field.name).visible) {
          this.inputsVisibility.find(x => x.key === field.name).visible = true;

          // @ts-ignore ignore type of validators set
          this.getFormControl(field.name).setValidators(validators);
          this.getFormControl(field.name).updateValueAndValidity();
          console.log(field.name, 'setValidators');
        }
        return true;
      }
    } else {
      return true;
    }
  }

  checkPasswords() {
    // here we have the 'passwords' group
    if (this.form.controls.password && this.form.controls.password_confirmation) {
      this.form.controls.password_confirmation.valueChanges.pipe(takeWhile(() => this.alive)).subscribe(() => {
        this.comparePasswords();
      });
      this.form.controls.password.valueChanges.pipe(takeWhile(() => this.alive)).subscribe(() => {
        this.comparePasswords();
      });
    }
  }

  comparePasswords() {
    const pass = this.form.controls.password.value;
    const password_confirmation = this.form.controls.password_confirmation.value;
    if (pass === password_confirmation) {
      this.form.controls.password_confirmation.setErrors(null);
    } else {
      this.form.controls.password_confirmation.setErrors({ passwordsDontMatch: true });
    }
  }

  checkValidations(key: any, props: ItemProps[] = this.service.featureProps) {
    // const props = this.service.featureProps;
    for (const field of props) {
      if (field.form) {
        if (field.form.Validators) {
          // console.log(field.form.Validators);
          if (field.form.Validators.find(x => x === key)) {
            return true;
          }
        }
        if (field.form.formFieldType == 'formArray') {
          this.checkValidations(key, field.form.arrayForm);
        }
      }
    }
  }

  checkImgSrc(field: any) {
    if (!!this.form.controls[field.name].valueChanges) {
      if (!this.previewImage) {
        if (!this.form.controls[field.name].value) {
          return field.form.placeHolder;
        } else {
          return this.form.controls[field.name].value;
        }
      } else {
        return this.previewImage;
      }
    }
  }

  ngOnDestroy(): void {
    this.service.clearActiveTabs();
    this.alive = false;
    this.form.reset();
  }

  checkRequiredFields(controlName: string) {
    if (!this.isEdit) {
      const abstractControl = this.form.controls[controlName];
      if (!abstractControl) {
        return false;
      }
      if (abstractControl.validator) {
        const validator = abstractControl.validator({} as AbstractControl);
        if (validator && validator.required) {
          return true;
        }
      }
      return false;
    }
  }

  focusInputById(id: any) {
    document.getElementById(id).focus();
  }

  cancelFormSubmission() {
    if (this.form.dirty) {
      this.service.openActionDialog(
        this.service,
        'cancel',
        'common.confirmCancelForm.title',
        'common.confirmCancelForm.message',
        'common.confirmCancelForm.submitText',
        'common.confirmCancelForm.cancelText',
        null,
        'bg-danger'
      );
    } else {
      // this.service.navigateToList();
      console.log(this.dialogRef);
      this.form.reset();
      this.closeDialog();
    }
  }

  groupItemsByGroup() {
    const tabGroups = this.groupByPipe.transform(this.service.featureProps, 'form.groupBy.tabGroup.tabGroupName');

    Object.entries(tabGroups).forEach(group => {
      let tabGroupName = group[0];
      if (tabGroupName === 'undefined') {
        tabGroupName = 'defaultGroup';
      }
      this.service.formInputsCategorized[tabGroupName] = {};

      const tabs = this.groupByPipe.transform(group[1], 'form.groupBy.tabGroup.tabName');
      Object.entries(tabs).forEach(tab => {
        let tabName = tab[0];
        if (tabName === 'undefined') {
          tabName = 'defaultTab';
        }
        this.service.formInputsCategorized[tabGroupName][tabName] = {};

        const tabContent = this.groupByPipe.transform(tab[1], 'form.groupBy.section');
        Object.entries(tabContent).forEach(section => {
          let sectionName = section[0];
          if (sectionName === 'undefined') {
            sectionName = 'defaultSection';
          }
          this.service.formInputsCategorized[tabGroupName][tabName][sectionName] = section[1];
        });
      });
    });
  }

  permissionsUpdateSubscription() {
    this.service.permissionsService.permissionsUpdated.pipe(takeWhile(() => this.alive)).subscribe(() => {
      this.service.setModulePermissions();
      this.unAuthRedirect();
    });
  }

  unAuthRedirect() {
    const permissionsKeys = this.activatedRoute.snapshot.data.permissionsKeys;
    if (permissionsKeys && permissionsKeys.length) {
      if (
        !permissionsKeys.every((permissionKey: { module?: string; key?: string }) => {
          console.log(this.service.permissionsService.getPermissions(permissionKey.key));
          return this.service.permissionsService.getPermissions(permissionKey.key);
        })
      ) {
        this.service.router.navigateByUrl('/401', { skipLocationChange: true });
      }
    }
  }

  showHideChanges(data: any) {
    if (data.field && data.validators) {
      // console.log(data.validators);

      this.form.get(data.field).setValidators(data.validators);

      // this.form.get(data.field).updateValueAndValidity();
      this.form.updateValueAndValidity();
      // this.changeDetectorRef.detectChanges();

      // console.log(this.form);
      // this.changeDetectorRef.detectChanges();
    }
  }

  clearInput() {
    this.service.clearInput.pipe(takeWhile(() => this.alive)).subscribe((input: any) => {
      this.form.controls[input].setValue(null);
    });
  }

  loadDummyData() {
    this.form.patchValue(this.refactorItem(this.service.returnDummyData(this.cid + '_form')));
  }

  UpdateFormFieldValue(event: { fieldProps?: ItemProps; fieldName?: string; fieldValue?: any; updateForm?: boolean }) {
    // console.log(event);
    const key =
      event && event.fieldProps && event.fieldProps.name
        ? event.fieldProps.name
        : event.fieldName
        ? event.fieldName
        : null;
    const value = event.fieldValue;
    if (key) {
      if (this.form.get(key)) {
        this.form.get(key).patchValue(value);
        if (event.updateForm) {
          console.log(2);

          this.form.markAsDirty();
        }
      }
    }
  }

  updateFormFieldSubscription() {
    this.service.updateInputValue
      .pipe(takeWhile(() => this.alive))
      .subscribe((event: { fieldProps?: ItemProps; fieldName?: string; fieldValue?: any; updateForm?: boolean }) => {
        this.UpdateFormFieldValue(event);
      });
  }

  actionOnCreatedItem(id: number) {
    return this.service.actionOnCreatedItem(id);
  }

  navigateToList() {
    this.service.navigateToList();
  }

  getInputGridSize(inputSize: string, gap: string) {
    if (inputSize) {
      if (gap) {
        return `0 1 calc(${inputSize} - ${gap})`;
      } else {
        return `0 1 ${inputSize}`;
      }
    } else {
      if (gap) {
        return `0 1 calc(100% - ${gap})`;
      } else {
        return `0 1 100%`;
      }
    }
  }

  sectionErrorsCount(tab: any) {
    let errorsCount = 0;
    Object.values(tab).forEach(section => {
      Object.values(section).forEach((field: ItemProps) => {
        if (
          this.form.controls[field.name].errors &&
          (this.form.dirty || this.form.invalid) &&
          (this.form.touched || this.isSubmitted)
        ) {
          errorsCount++;
        }
      });
    });
    // console.log(!!errorsCount);
    return errorsCount;
  }

  getLengthOfObject(object: any) {
    return Object.values(object).length;
  }
  groupTabCurrErrors(group: any, tabIndex: number): boolean {
    let errorsCount = 0;
    let enableSteps: boolean;
    Object.values(group).forEach((tab, index) => {
      Object.values(tab).forEach(section => {
        if (
          Object.values(section).some((field: ItemProps) => {
            if (field.form.groupBy.tabGroup.steps) {
              return true;
            }
          })
        ) {
          enableSteps = true;
        }
      });
    });
    if (enableSteps) {
      Object.values(group).forEach((tab, index) => {
        console.log('🚀 ~ CoreFormComponent ~ groupTabCurrErrors ~ tab:', tab);
        if (index === tabIndex) {
          Object.values(tab).forEach(section => {
            Object.values(section).forEach((field: ItemProps) => {
              if (this.form.controls[field.name].errors) {
                console.log(
                  '🚀 ~ CoreFormComponent ~ groupTabCurrErrors ~ this.form.controls[field.name].errors:',
                  this.form.controls[field.name].errors
                );
                console.log(field.name);

                this.form.controls[field.name].markAsTouched();
                errorsCount++;
              }
            });
          });
        }
      });
    }
    if (errorsCount === 0) {
      this.stepper.selected.completed = true;
      setTimeout(() => {
        // or do some API calls/ Async events
        this.stepper.next();
      }, 1);
    } else {
      this.stepper.selected.completed = false;
    }
    return errorsCount > 0;
  }

  groupTabCurrErrorsByKey(group: any, tabKey: string): boolean {
    let errorsCount = 0;
    let enableSteps: boolean;

    // Check if steps are enabled
    Object.values(group).forEach((tab, index) => {
      Object.values(tab).forEach(section => {
        if (
          Object.values(section).some((field: ItemProps) => {
            if (field.form.groupBy.tabGroup.steps) {
              return true;
            }
          })
        ) {
          enableSteps = true;
        }
      });
    });

    if (enableSteps) {
      // Find the tab by key and check for errors
      const tab = group[tabKey];
      if (tab) {
        Object.values(tab).forEach(section => {
          Object.values(section).forEach((field: ItemProps) => {
            if (this.form.controls[field.name].errors) {
              console.log(
                '🚀 ~ CoreFormComponent ~ groupTabCurrErrorsByKey ~ this.form.controls[field.name].errors:',
                this.form.controls[field.name].errors
              );
              console.log(field.name);

              this.form.controls[field.name].markAsTouched();
              errorsCount++;
            }
          });
        });
      }
    }

    if (errorsCount === 0) {
      this.stepper.selected.completed = true;
      setTimeout(() => {
        // or do some API calls/ Async events
        this.stepper.next();
      }, 1);
    } else {
      this.stepper.selected.completed = false;
    }
    return errorsCount > 0;
  }

  stepBack() {
    setTimeout(() => {
      // or do some API calls/ Async events
      this.stepper.previous();
    }, 1);
  }

  getDisplayedColumns(array: any[]) {
    // console.log(array.filter(x => x.list));
    return array.filter(x => x.list);
    // return [...Object.keys(array[0] || {})];
  }

  getFormControl(name: string, path: string = null) {
    // get the name string value from the mapper
    const fieldName = path || this.service.fieldMapper(name);
    // if not found return with form getter to throw form error
    // the name should be changed to a valid one then
    // if found but its a single value, return it
    if (!fieldName || fieldName.split('.').length == 1) return this.form.get(name);
    const map = fieldName.split('.');
    // map = [arrayname, index, control]
    const formGroup = this.form.get(map[0]) as FormArray;
    const formControl = formGroup.controls[+map[1]].get(map[2]);
    return formControl;
  }
  ServerValidationMapper(name: string, path: string = null) {
    // get the name string value from the mapper
    const fieldName = path || this.service.fieldMapper(name);
    // if not found return with form getter to throw form error
    // the name should be changed to a valid one then
    // if found but its a single value, return it
    let control: FormControl;
    if (!fieldName || fieldName.split('.').length == 1) {
      control = this.form.get(name) as FormControl;
    } else {
      const map = fieldName.split('.');
      // map = [arrayname, index, control]
      const formGroup = this.form.get(map[0]) as FormArray;
      const formControl = formGroup.controls[+map[1]].get(map[2]);
      control = formControl as FormControl;
    }
    // if(control) {
    //   control.setErrors({
    //     serverError: errors[prop]
    //   });
    // }
  }
  normalizeFlags() {
    this.isSubmitted = false;
  }
}
